import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Tag, Check, Copy, Link2, Crown, Users, Gift, Sparkles, Loader2, Pencil, BadgeCheck, Wand2, ImageIcon,
} from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n);

export default function Tarifs() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [interested, setInterested] = useState({});
  const [busyPlan, setBusyPlan] = useState(null);
  const [code, setCode] = useState("");
  const [editingCode, setEditingCode] = useState(false);
  const [codeDraft, setCodeDraft] = useState("");
  const [redeemCode, setRedeemCode] = useState("");

  const loadCfg = () => api.get("/billing/config").then((r) => { setCfg(r.data); setCode(r.data.affiliate_code || ""); }).catch(() => {});

  useEffect(() => {
    loadCfg();
    const p = new URLSearchParams(window.location.search);
    if (p.get("checkout") === "success") { toast.success("Abonnement activé, merci !"); window.history.replaceState({}, "", "/tarifs"); }
    if (p.get("checkout") === "cancel") { toast.info("Paiement annulé."); window.history.replaceState({}, "", "/tarifs"); }
  }, []);

  const checkout = async (lookupKey) => {
    setBusyPlan(lookupKey);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusyPlan(null);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const redeem = async () => {
    if (!redeemCode.trim()) return;
    try {
      const { data } = await api.post("/billing/redeem", { code: redeemCode.trim() });
      toast.success(data.message || "Code activé.");
      setRedeemCode("");
      loadCfg();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const refLink = code ? `${window.location.origin}/?ref=${code}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(refLink);
    toast.success("Lien de parrainage copié.");
  };

  const saveCode = async () => {
    try {
      const { data } = await api.put("/affiliate/code", { code: codeDraft });
      setCode(data.code);
      setEditingCode(false);
      const changed = data.code !== codeDraft.trim().toLowerCase();
      toast.success(changed ? `Code enregistré : « ${data.code} » (caractères spéciaux retirés).` : "Votre code de parrainage a été mis à jour.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const showInterest = async (planKey) => {
    setBusyPlan(planKey);
    try {
      await api.post("/interest", { plan: planKey });
      setInterested((s) => ({ ...s, [planKey]: true }));
      toast.success("Merci ! Nous vous recontacterons à l'ouverture.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyPlan(null);
    }
  };

  if (!cfg) return <div className="p-12 text-slate-400">Chargement…</div>;

  if (cfg.billing_mode === "off") {
    return (
      <div className="p-8 lg:p-12 max-w-2xl">
        <h1 className="font-serif text-4xl text-slate-900 mb-3">Offres</h1>
        <p className="text-slate-500">Les offres ne sont pas encore ouvertes. Revenez bientôt.</p>
      </div>
    );
  }

  const isDemo = cfg.billing_mode === "demo";
  const isActive = cfg.billing_mode === "active";
  const isMember = cfg.current_plan === "member";
  const sub = cfg.subscription;
  const subscribed = sub && ["active", "trialing"].includes(sub.status);
  const planIcon = { invite: Gift, member: Crown, solo: Sparkles, signature: Wand2, team: Users };

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <Tag className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Offres &amp; parrainage</span>
      </div>
      <h1 className="font-serif text-4xl text-slate-900 mb-2">Choisissez votre formule</h1>
      <p className="text-slate-500 mb-4">Tarifs affichés <strong>hors taxes (HT)</strong>. La TVA applicable est ajoutée au paiement.</p>

      <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 mb-6 max-w-3xl text-sm text-slate-600">
        <ImageIcon className="h-4 w-4 text-coral-dark shrink-0 mt-0.5" />
        <p>
          <strong className="text-slate-800">L'écriture est illimitée, les visuels sont comptés.</strong>{" "}
          Rédiger un post ne coûte presque rien : écrivez autant que vous voulez, affinez, recommencez.
          Un visuel IA, surtout une affiche photoréaliste à votre image, demande beaucoup plus de calcul.
          C'est ce que mesurent les quotas ci-dessous.
        </p>
      </div>

      {isDemo && (
        <div data-testid="demo-banner" className="flex items-center gap-2.5 bg-coral-soft border border-coral/30 rounded-sm px-4 py-3 mb-8 text-sm text-coral-dark">
          <BadgeCheck className="h-4 w-4 shrink-0" />
          Aperçu avant ouverture : indiquez les offres qui vous intéressent, nous vous recontacterons en priorité.
        </div>
      )}

      {/* Statut abonnement / essai */}
      {subscribed && (
        <div data-testid="sub-status" className="flex items-center gap-2.5 bg-green-50 border border-green-200 rounded-sm px-4 py-3 mb-6 text-sm text-green-800">
          <BadgeCheck className="h-4 w-4 shrink-0" />
          {sub.status === "trialing" ? "Essai en cours" : "Abonnement actif"}, plan {sub.plan} ({sub.period === "year" ? "annuel" : "mensuel"}).
        </div>
      )}

      {/* Redeem code */}
      <div data-testid="redeem-card" className="flex flex-col sm:flex-row sm:items-end gap-3 bg-white border border-slate-200 rounded-sm p-4 mb-8 max-w-xl">
        <div className="flex-1">
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">J'ai un code de réunion</label>
          <input data-testid="redeem-input" value={redeemCode} onChange={(e) => setRedeemCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && redeem()}
            placeholder="Ex. APOLLO2026" className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral uppercase" />
        </div>
        <button data-testid="redeem-btn" onClick={redeem}
          className="bg-ink text-white px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-ink/90 transition-colors">Activer</button>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center gap-3 mb-6">
        <span className={`text-sm font-semibold ${!yearly ? "text-ink" : "text-slate-400"}`}>Mensuel</span>
        <button data-testid="billing-period-toggle" onClick={() => setYearly((v) => !v)}
          className="relative w-12 h-6 rounded-full transition-colors bg-coral-strong">
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${yearly ? "left-6" : "left-0.5"}`} />
        </button>
        <span className={`text-sm font-semibold ${yearly ? "text-ink" : "text-slate-400"}`}>Annuel <span className="text-coral-dark">(2 mois offerts)</span></span>
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-14" data-testid="plans-grid">
        {cfg.plans.map((p) => {
          const Icon = planIcon[p.key] || Sparkles;
          const highlight = !!p.highlight;
          // Formule payante : prix affiché, bascule mensuel/annuel, paiement Stripe.
          const paid = p.cta === "buy" && p.price_ht > 0;
          const subscribedToThis = subscribed && sub.plan === p.key;
          return (
            <div key={p.key} data-testid={`plan-${p.key}`}
              className={`relative flex flex-col rounded-lg border p-6 transition-shadow hover:shadow-md ${highlight ? "border-coral bg-white ring-1 ring-coral/30" : "border-slate-200 bg-white"}`}>
              {highlight && <span className="absolute -top-2.5 left-6 text-[10px] font-bold uppercase tracking-wider bg-coral-strong text-white px-2 py-0.5 rounded-full">Recommandé</span>}
              <Icon className={`h-5 w-5 mb-3 ${highlight ? "text-coral-dark" : "text-slate-400"}`} />
              <h3 className="font-serif text-2xl text-slate-900">{p.name}</h3>
              <p className="text-xs text-slate-500 mt-1 mb-4 min-h-[32px]">{p.tagline}</p>

              <div className="mb-4">
                {paid ? (
                  <p className="flex items-baseline gap-1">
                    <span className="font-serif text-3xl text-ink">{yearly ? `${fmt(p.price_ht_year)} €` : `${fmt(p.price_ht)} €`}</span>
                    <span className="text-sm text-slate-400">HT / {yearly ? "an" : "mois"}</span>
                  </p>
                ) : p.price_ht === 0 ? (
                  <p className="font-serif text-3xl text-ink">Gratuit</p>
                ) : (
                  <p className="font-serif text-2xl text-slate-400">Sur devis</p>
                )}
                {paid && p.period && <p className="text-xs text-slate-400 mt-0.5">{yearly ? "Facturé annuellement" : "Sans engagement"}</p>}
              </div>

              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="h-4 w-4 text-coral-dark shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {isMember && p.key === "member" ? (
                <div data-testid={`plan-cta-${p.key}`} className="rounded-sm bg-coral-soft border border-coral/30 px-3 py-2.5 text-center">
                  <p className="text-xs font-bold text-coral-dark flex items-center justify-center gap-1.5"><Crown className="h-3.5 w-3.5" /> Votre formule actuelle</p>
                  <p className="text-[11px] text-coral-dark/80 mt-0.5">offerte avec votre adhésion APOLLO</p>
                </div>
              ) : p.cta === "soon" ? (
                <button disabled data-testid={`plan-cta-${p.key}`} className="w-full py-2.5 rounded-sm text-sm font-semibold bg-slate-100 text-slate-400 cursor-not-allowed">Bientôt disponible</button>
              ) : isDemo ? (
                <button data-testid={`plan-cta-${p.key}`} onClick={() => showInterest(p.key)} disabled={busyPlan === p.key || interested[p.key]}
                  className={`w-full py-2.5 rounded-sm text-sm font-semibold transition-colors ${interested[p.key] ? "bg-green-50 text-green-700 border border-green-200" : highlight ? "bg-coral-strong text-white hover:bg-coral-hover" : "border border-coral text-coral-dark hover:bg-coral-strong hover:text-white"}`}>
                  {busyPlan === p.key ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : interested[p.key] ? "Intérêt enregistré ✓" : "Je suis intéressé"}
                </button>
              ) : isActive && paid ? (
                subscribedToThis ? (
                  <button data-testid={`plan-cta-${p.key}`} onClick={openPortal}
                    className="w-full py-2.5 rounded-sm text-sm font-semibold border border-coral text-coral-dark hover:bg-coral-strong hover:text-white transition-colors">
                    Gérer mon abonnement
                  </button>
                ) : (
                  <button data-testid={`plan-cta-${p.key}`}
                    onClick={() => checkout(yearly ? p.lookup_yearly : p.lookup_monthly)} disabled={!!busyPlan}
                    className={`w-full py-2.5 rounded-sm text-sm font-semibold transition-colors disabled:opacity-60 ${highlight ? "bg-coral-strong text-white hover:bg-coral-hover" : "border border-coral text-coral-dark hover:bg-coral-strong hover:text-white"}`}>
                    {busyPlan ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : subscribed ? "Changer pour cette formule" : "S'abonner"}
                  </button>
                )
              ) : isActive && p.key === "member" ? (
                <div data-testid="plan-cta-member" className="text-center text-xs text-slate-500 py-2.5 px-2 border border-dashed border-slate-200 rounded-sm">
                  {user?.membre_apollo?.statut === "non_reconnu" ? "Votre adresse n'est pas reconnue comme membre APOLLO. Utilisez l'adresse e-mail de votre compte APOLLO, ou contactez support@apollofrance.com."
                    : "Activé automatiquement avec l'adresse e-mail de votre compte APOLLO"}
                </div>
              ) : isActive && p.key === "invite" ? (
                <div data-testid={`plan-cta-${p.key}`} className="text-center text-xs text-slate-400 py-2.5 border border-dashed border-slate-200 rounded-sm">Activation par code ci-dessous</div>
              ) : (
                <button disabled data-testid={`plan-cta-${p.key}`} className="w-full py-2.5 rounded-sm text-sm font-semibold bg-slate-100 text-slate-400 cursor-not-allowed">Bientôt</button>
              )}
            </div>
          );
        })}
      </div>

      {/* Affiliation */}
      <div className="bg-ink text-white rounded-lg p-8 max-w-3xl" data-testid="affiliate-card">
        <div className="flex items-center gap-2 text-coral mb-1">
          <Link2 className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Programme de parrainage</span>
        </div>
        <h3 className="font-serif text-2xl mb-2">Parrainez, gagnez</h3>
        <p className="text-white/70 text-sm mb-5 max-w-xl">
          Partagez votre lien personnel. Toute inscription issue de votre lien vous sera attribuée (au 1er clic, fenêtre 60 jours).
        </p>

        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-white/50">Votre lien</label>
        <div className="flex items-center gap-2 mt-1.5 mb-4">
          <input data-testid="affiliate-link" readOnly value={refLink}
            className="flex-1 bg-white/10 border border-white/15 rounded-sm px-3.5 py-2.5 text-sm text-white outline-none" />
          <button data-testid="copy-link-btn" onClick={copyLink}
            className="flex items-center gap-2 bg-coral-strong text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors">
            <Copy className="h-4 w-4" /> Copier
          </button>
        </div>

        {editingCode ? (
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-white/10 border border-white/15 rounded-sm px-3">
              <span className="text-sm text-white/50">?ref=</span>
              <input data-testid="code-input" autoFocus value={codeDraft} onChange={(e) => setCodeDraft(e.target.value)}
                className="bg-transparent py-2.5 text-sm text-white outline-none w-40" placeholder="votre-nom" />
            </div>
            <button data-testid="save-code-btn" onClick={saveCode} className="bg-coral-strong text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors">Enregistrer</button>
            <button onClick={() => setEditingCode(false)} className="text-white/50 text-sm hover:text-white">Annuler</button>
          </div>
        ) : (
          <button data-testid="edit-code-btn" onClick={() => { setCodeDraft(code); setEditingCode(true); }}
            className="flex items-center gap-1.5 text-sm text-white/70 hover:text-coral transition-colors">
            <Pencil className="h-3.5 w-3.5" /> Personnaliser mon code (actuel : <span className="font-semibold text-white">{code}</span>)
          </button>
        )}

        <div className="mt-5 pt-5 border-t border-white/10">
          <a href="/parrainage" data-testid="goto-affiliate-dashboard"
            className="inline-flex items-center gap-2 text-sm font-semibold text-coral hover:text-coral-hover transition-colors">
            <BadgeCheck className="h-4 w-4" /> Voir mon tableau de bord de parrainage (gains, filleuls, virements)
          </a>
        </div>
      </div>
    </div>
  );
}
